import { useCallback, useEffect, useRef, useState } from 'react';

import { Company } from '@/features/companies/types/table/company.types';
import { companiesService } from '@/features/companies/services/companies.service';
import { useCompanySearchStore } from '@/features/companies/stores/company-search.store';

export const useCompanies = () => {
  const [searchResults, setSearchResults] = useState<Company[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  // Get state from global store
  const {
    searchInputValues,
    committedSearchParams,
    currentPage,
    itemsPerPage,
    sortField,
    sortOrder,
    setSearchInputValues,
    setCommittedSearchParams,
    setCurrentPage,
    setItemsPerPage,
    setSortField,
    setSortOrder,
    clearSearch,
  } = useCompanySearchStore();

  // Use refs to store latest values to avoid stale closure
  const currentPageRef = useRef(currentPage);
  const itemsPerPageRef = useRef(itemsPerPage);
  const searchParamsRef = useRef(committedSearchParams);
  const sortFieldRef = useRef(sortField);
  const sortOrderRef = useRef(sortOrder);
  // Ref to store autocomplete clear function
  const autocompleteClearRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    itemsPerPageRef.current = itemsPerPage;
  }, [itemsPerPage]);

  useEffect(() => {
    searchParamsRef.current = committedSearchParams;
  }, [committedSearchParams]);

  useEffect(() => {
    sortFieldRef.current = sortField;
  }, [sortField]);

  useEffect(() => {
    sortOrderRef.current = sortOrder;
  }, [sortOrder]);

  const handleSearchCompanies = useCallback(async (params: Record<string, unknown> = {}) => {
    setIsSearching(true);
    try {
      // Use params passed in, or fallback to searchParamsRef.current
      const searchParamsToUse = Object.keys(params).length > 0 ? params : searchParamsRef.current;

      // Convert sortField to API format
      let apiSortField = sortFieldRef.current;
      if (sortFieldRef.current === 'updatedAt') apiSortField = 'updatedAt';
      else if (sortFieldRef.current === 'createdAt') apiSortField = 'createdAt';

      const apiParams = {
        ...searchParamsToUse,
        page: currentPageRef.current,
        perPage: itemsPerPageRef.current,
        sort: apiSortField,
        orderBy: sortOrderRef.current,
      };

      const response = await companiesService.getCompanies(apiParams);
      const companiesData = Array.isArray(response?.data?.data?.data)
        ? response.data.data.data
        : [];
      const paginationData = (response?.data as { data?: { pagination?: { total?: number } } })
        ?.data?.pagination;

      // Always update search results, even if empty array
      // Create new array reference to ensure React detects the change
      // Always create a new array reference to ensure React detects the change
      setSearchResults([...companiesData]);

      // Always update pagination info
      setTotalItems(paginationData?.total || 0);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);
  // Handle sort change
  const handleSortChange = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      // Store the original field for UI consistency
      setSortField(field);
      setSortOrder(order);
    },
    [setSortField, setSortOrder]
  );

  const handleClear = useCallback(() => {
    clearSearch();
    // Clear autocomplete value
    if (autocompleteClearRef.current) {
      autocompleteClearRef.current();
    }
  }, [clearSearch]);

  // Register autocomplete clear function
  const handleRegisterAutocompleteClear = (clearFn: () => void) => {
    autocompleteClearRef.current = clearFn;
  };

  // Handle search from GlobalSearch component
  const handleSearch = useCallback(
    (params: Record<string, unknown>, preservePage = false) => {
      setCommittedSearchParams(params);
      setSearchInputValues(params);
      // Only reset to page 1 if it's a new search, preserve page if restoring
      if (!preservePage) {
        setCurrentPage(1); // Reset to first page when searching
      }
      // Note: useEffect will trigger API call when committedSearchParams or currentPage changes
    },
    [setCommittedSearchParams, setSearchInputValues, setCurrentPage]
  );

  // Handle search params change in real-time (save input values to store)
  const handleSearchChange = useCallback(
    (params: Record<string, unknown>) => {
      setSearchInputValues(params);
    },
    [setSearchInputValues]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  // Handle items per page change
  const handleItemsPerPageChange = useCallback(
    (newItemsPerPage: number) => {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1); // Reset to first page when changing items per page
    },
    [setItemsPerPage, setCurrentPage]
  );

  // Call API when pagination, sorting, or committed search params change
  // Similar to members page - always trigger when dependencies change
  useEffect(() => {
    // Always call API when dependencies change, even if committedSearchParams is empty
    // This ensures data is loaded when navigating back or changing page
    void handleSearchCompanies({});
  }, [
    currentPage,
    itemsPerPage,
    sortField,
    sortOrder,
    committedSearchParams,
    handleSearchCompanies,
  ]);

  return {
    searchResults,
    isSearching,
    currentPage,
    itemsPerPage,
    totalItems,
    searchParams: searchInputValues, // Return input values for form fields
    committedSearchParams,
    sortField,
    sortOrder,
    handleSortChange,
    handleSearch,
    handleSearchChange,
    handleClear,
    handlePageChange,
    handleItemsPerPageChange,
    handleRegisterAutocompleteClear,
  };
};
